import axios from "axios";
import semver from "semver";
import { checkServerHealth } from "./api/health";
import ConsoleHelper from "./utils/ConsoleHelper";

import {
  setAccessToken,
  clearAccessToken,
  getAccessToken,
} from "utils/authToken";
import {
  setServerDown,
  setUpdateAvailable,
  setMinimumVersionNotMet,
} from "store/actions/appActions";
import store from "./store";

const THIRTY_MIN_IN_MS = 30 * 60 * 1000;
const channel = new BroadcastChannel("app-version-channel");

function hasReloadedRecently() {
  const reloadTriggeredTime = window.localStorage.getItem(
    "force-reload-triggered-time"
  );
  return (
    reloadTriggeredTime &&
    Date.now() - parseInt(reloadTriggeredTime) < THIRTY_MIN_IN_MS
  );
}

const AUTH_PATHS = new Set([
  "/auth/refresh",
  "/auth/logout",
  "/auth/v2/login",
  "/auth/signup",
  "/auth/signup/:token",
]);

// Create our custom Axios instance without withCredentials by default
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

// -------------------------
// Response Interceptor
// -------------------------
axiosInstance.interceptors.response.use(
  (response) => {
    const currentVersion = process.env.REACT_APP_VERSION;
    const liveVersion = response.headers["x-version"];
    const minVersionRequired = response.headers["x-min-version"];

    if (liveVersion && currentVersion) {
      const isServerVersionNewer = semver.gt(liveVersion, currentVersion);
      let minVersionRequiredMet = true;
      if (minVersionRequired) {
        minVersionRequiredMet = semver.gte(currentVersion, minVersionRequired);
      }
      if (isServerVersionNewer && !minVersionRequiredMet) {
        if (!hasReloadedRecently()) {
          window.localStorage.setItem(
            "force-reload-triggered-time",
            Date.now().toString()
          );
          ConsoleHelper(
            "Reloading due to version update / min version not met"
          );
          channel.postMessage({ action: "reload" });
          window.location.reload();
        } else {
          ConsoleHelper(
            "Minimum version not met but recently reloaded. Handle in UI if needed."
          );
        }
      }
    }

    // --- Added missing code ---
    // Update the updateAvailable state
    const isServerVersionNewer =
      liveVersion && currentVersion
        ? semver.gt(liveVersion, currentVersion)
        : false;
    const currentUpdateAvailable = store.getState().app.updateAvailable;
    if (isServerVersionNewer !== currentUpdateAvailable) {
      store.dispatch(setUpdateAvailable(isServerVersionNewer));
    }
    // Reset the serverDown state if needed
    if (store.getState().app.serverDown) {
      store.dispatch(setServerDown(false));
    }
    // --- End of added code ---

    return response;
  },
  async (error) => {
    // --- Added minimum version handling ---
    if (error.response && error.response.data) {
      const { message, code } = error.response.data;
      const status = error.response.status;
      if (status === 426) {
        console.log("Minimum version not met");
        if (!hasReloadedRecently()) {
          window.localStorage.setItem(
            "force-reload-triggered-time",
            Date.now().toString()
          );
          console.log("Reloading due to minimum version not met");
          channel.postMessage({ action: "reload" });
          window.location.reload();
        } else {
          console.log("Dispatch minimum version not met");
          store.dispatch(setMinimumVersionNotMet(true));
        }
        return Promise.reject(error);
      }
    }
    // --- End of added minimum version handling ---
    if (!error.response) {
      if (!navigator.onLine) {
        ConsoleHelper("No internet connection available.");
      } else if (error.code === "ECONNABORTED") {
        ConsoleHelper("Request timed out:", error.message);
      } else if (error.message && error.message.includes("Network Error")) {
        ConsoleHelper("Network Error:", error.message);
        await checkServerHealth();
      } else {
        ConsoleHelper("Unknown error without response:", error.message);
      }
      return Promise.reject(error);
    }

    const { status } = error.response;
    const originalConfig = error.config;

    if (originalConfig.__isRetryRequest) {
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      originalConfig.url !== "/auth/refresh" &&
      originalConfig.url !== "/auth/login"
    ) {
      originalConfig.__isRetryRequest = true;
      try {
        // Explicitly use withCredentials only for the refresh endpoint
        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        ConsoleHelper("Refreshed token:", refreshResponse.data);
        setAccessToken(refreshResponse.data.accessToken);

        return axiosInstance(originalConfig);
      } catch (refreshError) {
        ConsoleHelper("Refresh token failed:", refreshError);
        if (window.location.pathname !== "/auth/login") {
          window.location.href = "/auth/login";
        }
        return Promise.reject(refreshError);
      }
    }

    if (status === 401) {
      if (window.location.pathname !== "/auth/login") {
        window.location.href = "/auth/login";
      }
    }

    // Is it a subscription issue? check message
    if (status === 403) {
      const message = error.response.data.message || error.response.data.error;
      if (message && message.includes("Subscription")) {
        // Redirect to subscription page only if not already there
        if (window.location.pathname !== "/subscriptionV2") {
          ConsoleHelper("Subscription issue detected, redirecting...");
          window.location.href = "/subscriptionV2";
        }
      }
      if (message.includes("verified")) {
        // Redirect to verify page only if not already there
        if (window.location.pathname !== "/auth/verify") {
          window.location.href = "/auth/verify";
        }
      }
    }

    return Promise.reject(error);
  }
);

// -------------------------
// Request Interceptor
// -------------------------
// Interceptor #1 → adds version + Authorization
axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["frontend-app-version"] = process.env.REACT_APP_VERSION;
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default axiosInstance;
